/**
 * Top Cup blog — progressive enhancement for the post template.
 *
 * Everything here is optional. With JavaScript unavailable the post still
 * reads correctly: the jump-link nav stays hidden, captions stay in their
 * own labelled block below the article, and the sticky bar never appears.
 *
 *   1. Jump links   — builds "On this page" from the H2s in the post body.
 *   2. Reel captions — pairs each caption with its reel when the counts match.
 *   3. Sticky bar   — shows a slim product bar once the reader is into the body.
 */

const slugify = (text, used) => {
  const base =
    text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section';

  let slug = base;
  let n = 2;
  while (used.has(slug)) slug = `${base}-${n++}`;
  used.add(slug);
  return slug;
};

function buildJumpLinks() {
  const nav = document.querySelector('[data-tc-toc]');
  const content = document.querySelector('[data-tc-article-content]');
  if (!nav || !content) return;

  const list = nav.querySelector('[data-tc-toc-list]');
  const min = parseInt(nav.dataset.minHeadings || '3', 10);
  const headings = [...content.querySelectorAll('h2')].filter(
    (h) => h.textContent.trim().length > 0
  );

  if (!list || headings.length < min) return;

  const used = new Set(
    [...document.querySelectorAll('[id]')].map((el) => el.id)
  );

  const fragment = document.createDocumentFragment();

  headings.forEach((heading) => {
    if (!heading.id) heading.id = slugify(heading.textContent.trim(), used);

    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = `#${heading.id}`;
    a.textContent = heading.textContent.trim();
    li.appendChild(a);
    fragment.appendChild(li);
  });

  list.appendChild(fragment);
  nav.hidden = false;
}

function pairReelCaptions() {
  const block = document.querySelector('[data-tc-captions]');
  const content = document.querySelector('[data-tc-article-content]');
  if (!block || !content) return;

  const captions = [...block.querySelectorAll('[data-tc-caption]')];
  // The house reel embed is a card wrapping a .tc-ig-video element.
  const videos = [...content.querySelectorAll('video.tc-ig-video')];
  if (!captions.length || captions.length !== videos.length) return;

  videos.forEach((video, index) => {
    const card = video.closest('div');
    const caption = captions[index];
    if (!card || !caption || !card.parentElement) return;

    const row = document.createElement('div');
    row.className = 'tc-reel-row';
    card.parentElement.insertBefore(row, card);
    row.appendChild(card);

    caption.classList.add('tc-caption--paired');
    row.appendChild(caption);
  });

  // Every caption has been moved next to its reel, so the standalone
  // block is now empty of content and only its heading would remain.
  block.hidden = true;
}

function initStickyBar() {
  const bar = document.querySelector('[data-tc-sticky]');
  const content = document.querySelector('[data-tc-article-content]');
  if (!bar || !content || !('IntersectionObserver' in window)) return;

  let dismissed = false;
  try {
    dismissed = sessionStorage.getItem('tc-sticky-dismissed') === '1';
  } catch (error) {
    dismissed = false;
  }
  if (dismissed) return;

  bar.hidden = false;

  const observer = new IntersectionObserver(
    ([entry]) => {
      // Show it once the reader is actually reading, not on arrival.
      const show = entry.boundingClientRect.top < 0;
      bar.dataset.visible = show ? 'true' : 'false';
    },
    { rootMargin: '-25% 0px 0px 0px', threshold: 0 }
  );

  observer.observe(content);

  bar.querySelector('[data-tc-sticky-close]')?.addEventListener('click', () => {
    bar.dataset.visible = 'false';
    observer.disconnect();
    try {
      sessionStorage.setItem('tc-sticky-dismissed', '1');
    } catch (error) {
      /* private mode or blocked storage — the bar simply stays dismissed for this view */
    }
  });
}

function init() {
  buildJumpLinks();
  pairReelCaptions();
  initStickyBar();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
